'use client'

import React, { useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import classNames from 'classnames'
import { HotelData } from '@/types/Misc'

const ACTIVE_CLASSES = ['border-primary']
const INACTIVE_CLASSES = ['border-white']
const SCROLL_PADDING = ['scroll-pt-[86px]', 'md:scroll-pt-[98px]']

interface Target {
  target: string
  offset: number
}

function NavigationItem(props: { label: string; active: boolean; additionalClassName?: string }) {
  const t = useTranslations('Hotel.Navigation')

  return (
    <a
      id={`${props.label}-navigationItemId`}
      href={`#${props.label}`}
      className={classNames(
        'snap-start whitespace-nowrap border-b-4 py-4',
        props.additionalClassName,
        {
          'border-white': !props.active,
          'border-primary': props.active,
        },
      )}
    >
      {t(props.label)}
    </a>
  )
}

export function Navigation(props: { hotel: HotelData }) {
  const navigationRef = useRef<HTMLDivElement>(null)
  const t = useTranslations('Hotel.Navigation')

  useEffect(() => {
    SCROLL_PADDING.forEach((p) => window.document.documentElement.classList.add(p))

    function handleScroll() {
      if (!navigationRef.current) {
        return
      }

      const offSets = getOffsets()
      const passedSections = offSets.filter((offset) => offset.offset - 100 <= window.scrollY)
      if (passedSections.length >= 2) {
        document.getElementById('scrollButton')?.classList.add('block')
        document.getElementById('scrollButton')?.classList.remove('hidden')
      } else {
        document.getElementById('scrollButton')?.classList.add('hidden')
        document.getElementById('scrollButton')?.classList.remove('block')
      }
      if (passedSections.length > 0) {
        const active = navigationRef.current.querySelector('a.border-primary')
        const newActive = navigationRef.current.querySelector(
          `#${(passedSections[passedSections.length - 1] || offSets[0]).target}-navigationItemId`,
        )
        if (active !== newActive) {
          active?.classList.remove(...ACTIVE_CLASSES)
          active?.classList.add(...INACTIVE_CLASSES)
          newActive?.classList.add(...ACTIVE_CLASSES)
          newActive?.classList.remove(...INACTIVE_CLASSES)

          scrollToElementHorizontally(newActive as HTMLElement, navigationRef.current)
        }
      }
    }

    handleScroll()

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // get all the links and get the page offset of the corresponding sections
  function getOffsets(): Target[] {
    if (!navigationRef.current) {
      return []
    }

    const links = navigationRef.current.querySelectorAll('a:not(#scrollButton)')

    return Array.from(links)
      .map(getOffset)
      .filter((v) => !!v) as Target[]
  }

  return (
    <div className="relative">
      <div
        ref={navigationRef}
        className="no-scrollbar relative flex w-full snap-x snap-mandatory scroll-px-5 gap-12 overflow-hidden overflow-x-scroll text-lg font-bold leading-tight text-stone-900"
      >
        <NavigationItem label={'offers'} active={true} additionalClassName="ml-5" />
        <NavigationItem label={'hotel'} active={false} />
        {props.hotel.rooms && props.hotel.rooms.length > 0 && (
          <NavigationItem label={'rooms'} active={false} />
        )}

        {props.hotel.ratingValue > 0 && <NavigationItem label={'ratings'} active={false} />}
        <NavigationItem label={'location'} active={false} additionalClassName="mr-5" />

        <div className="flex grow items-center justify-end pr-5">
          <a
            href="#offers"
            id="scrollButton"
            className="fixed bottom-5 left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-primary px-6 py-4 font-semibold text-white lg:relative lg:bottom-0 lg:left-0 lg:translate-x-0 lg:transform lg:py-2"
          >
            {t('buttonText')}
          </a>
        </div>
      </div>
      <div className="absolute inset-y-0 left-0 w-5 bg-gradient-to-r from-white"></div>
      <div className="absolute inset-y-0 right-0 w-5 bg-gradient-to-l from-white"></div>
    </div>
  )
}

function scrollToElementHorizontally(element: HTMLElement, scrollContainer?: HTMLElement): void {
  // Use the provided scrollContainer or default to the element's parent if not provided
  const container = scrollContainer || element.parentElement

  if (container) {
    // Get the current vertical scroll position to maintain it
    const currentScrollTop = container.scrollTop

    // Calculate the target horizontal scroll position
    const elementRect = element.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    const scrollLeft = elementRect.left - containerRect.left + container.scrollLeft

    // Scroll horizontally without changing the vertical scroll position
    container.scrollTo({
      top: currentScrollTop, // Preserve the vertical scroll position
      left: scrollLeft, // Adjust to the calculated horizontal position
      behavior: 'smooth', // Smooth scrolling (optional, can be 'auto')
    })
  } else {
    console.warn('Container not found')
  }
}

function getOffset(link: HTMLAnchorElement) {
  const target = link.getAttribute('href')?.replace('#', '')
  const element = document.getElementById(target || '')

  if (!element) {
    return
  }

  return {
    target,
    offset: element.offsetTop,
  }
}
